import { UserCircleIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useMemo, useState } from 'react';
import { useHref, useNavigate } from 'react-router-dom';

import { EntityTooltip } from '@/components/entity-links/core/EntityTooltip';
import type { FetchState } from '@/components/entity-links/core/types';
import {
  type Firm,
  FirmTooltipContent,
} from '@/components/entity-links/entities/FirmLink';
import { UserLink } from '@/components/entity-links/entities/UserLink';
import { InfoRow, TooltipSection } from '@/components/entity-links/primitives';
import { cn } from '@/lib/utils';
import { enhancedApi } from '@/services/api/generated/firmsService';

interface CaseManagerInfo {
  id: string;
  display_name: string;
}

interface MatterFirmLinkProps {
  firmId: string;
  caseManager?: CaseManagerInfo | null;
  className?: string;
  children: React.ReactNode;
}

const MatterFirmTooltipContent = memo<{
  fetchState: FetchState<Firm>;
  isPersistent: boolean;
  caseManager?: CaseManagerInfo | null;
}>(({ fetchState, isPersistent, caseManager }) => {
  if (fetchState.status !== 'success') {
    return (
      <FirmTooltipContent fetchState={fetchState} isPersistent={isPersistent} />
    );
  }

  return (
    <div>
      <FirmTooltipContent fetchState={fetchState} isPersistent={isPersistent} />
      {caseManager && (
        <TooltipSection withBorder>
          <InfoRow
            icon={UserCircleIcon}
            label="Case Manager"
            value={
              <UserLink entityId={caseManager.id} side="left">
                {caseManager.display_name}
              </UserLink>
            }
          />
        </TooltipSection>
      )}
    </div>
  );
});

MatterFirmTooltipContent.displayName = 'MatterFirmTooltipContent';

export const MatterFirmLink = memo(
  ({ firmId, caseManager, className, children }: MatterFirmLinkProps) => {
    const navigate = useNavigate();
    const [shouldFetch, setShouldFetch] = useState(false);

    const { data, isLoading, isFetching, isError, error, isSuccess } =
      enhancedApi.endpoints.firmsRetrieve.useQuery(
        { id: firmId },
        { skip: !shouldFetch },
      );

    const fetchState = useMemo<FetchState<Firm>>(() => {
      if (isError && error) return { status: 'error', error };
      if (isSuccess && data) return { status: 'success', data };
      if (isLoading || isFetching) return { status: 'pending' };
      return { status: 'idle' };
    }, [isLoading, isFetching, isError, isSuccess, error, data]);

    const detailUrl = `/firms/${firmId}`;
    const href = useHref(detailUrl);

    const handleClick = (e: React.MouseEvent) => {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      navigate(detailUrl);
    };

    const tooltipContentRenderer = useCallback(
      (isPersistent: boolean) => (
        <MatterFirmTooltipContent
          fetchState={fetchState}
          isPersistent={isPersistent}
          caseManager={caseManager}
        />
      ),
      [fetchState, caseManager],
    );

    return (
      <EntityTooltip
        content={tooltipContentRenderer}
        side="right"
        align="center"
        openDelay={200}
        onOpenChange={(open) => {
          if (open) setShouldFetch(true);
        }}
      >
        <a
          href={href}
          onClick={handleClick}
          className={cn(
            'inline-flex cursor-pointer items-center gap-0.5',
            'transition-colors',
            className,
          )}
          data-entity-id={firmId}
          data-entity-type="FirmLink"
        >
          {children}
        </a>
      </EntityTooltip>
    );
  },
);

MatterFirmLink.displayName = 'MatterFirmLink';
